import axios from 'axios';
import to from 'await-to-js';
import { filter, isEmpty } from 'lodash';
import { ascending } from '@enums/sort-direction';
import { competitorSources } from '@enums/source-types';
import sortCompetitors from '../utils/sort-competitors';
import { displayStoreGroupCompetitors } from '@enums/feature-flags';

const getInitialState = () => {
  return {
    competitors: [],
    loading: false,
    runningCompetitorPriceSimulation: false,
    competitorKeyToTypeMap: {},
  };
};

const store = {
  namespaced: true,

  state: {
    ...getInitialState(),
    competitorOrderConfig: {},
  },

  getters: {
    competitorDescriptionToDisplayDescriptionMap: state => {
      if (isEmpty(state.competitors)) return {};
      return state.competitors.reduce((result, competitor) => {
        result[competitor.competitorDescription] = competitor.competitorDisplayDescription;
        return result;
      }, {});
    },

    nonStoreGroupCompetitors: state => {
      return filter(state.competitors, c => c.competitorType !== competitorSources.storeGroup);
    },

    feedCompetitors: (state, getters) => {
      return filter(getters.competitors, c => c.competitorType === competitorSources.feed);
    },

    feedCompetitorNames: (state, getters) => {
      return getters.feedCompetitors.map(c => c.competitorDescription);
    },

    competitors: (state, getters, rootState) => {
      // Filter store-group competitors out if displayStoreGroupCompetitors not enabled
      if (!rootState.clientConfig.toggleLogic[displayStoreGroupCompetitors]) {
        return getters.nonStoreGroupCompetitors;
      }

      return state.competitors;
    },

    competitorsForExport: (state, getters, rootState) => {
      // Regardless of displayStoreGroupCompetitors, return all non-storegroup competitors along with
      // storegroup competitors configured in storeGroupCompetitorsToExport
      const storeGroupCompetitorsToExport =
        rootState.clientConfig.toolStoreGroupConfig.storeGroupCompetitorsToExport;

      return filter(
        state.competitors,
        c =>
          c.competitorType !== competitorSources.storeGroup ||
          storeGroupCompetitorsToExport.includes(c.competitorDisplayDescription)
      );
    },
  },

  mutations: {
    setCompetitorMetadata(state, competitorMetadata) {
      state.competitors = competitorMetadata;
    },

    setLoading(state, isLoading) {
      state.loading = isLoading;
    },

    setRunningCompetitorPriceSimulation(state, runningCompetitorPriceSimulation) {
      state.runningCompetitorPriceSimulation = runningCompetitorPriceSimulation;
    },

    resetState(state) {
      Object.assign(state, getInitialState());
    },

    setCompetitorKeyToTypeMap(state, competitorMetadata) {
      state.competitorKeyToTypeMap = competitorMetadata.reduce((acc, comp) => {
        acc[comp.competitorKey] = comp.competitorType;
        return acc;
      }, {});
    },

    setCompetitorOrderConfig(state, competitorOrderConfig) {
      state.competitorOrderConfig = competitorOrderConfig;
    },
  },

  actions: {
    async fetchCompetitorMetadata({ commit, dispatch, state, getters, rootState }) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      if (!workpackageId) return;
      commit('setLoading', true);
      const params = {
        sortDirection: ascending,
        sortBy: 'competitorDisplayDescription',
      };
      const [err, { data: competitorMetadata }] = await to(
        axios.get(`/api/competitor-metadata/workpackage/${workpackageId}`, { params })
      );
      if (err) {
        commit('setLoading', false);
        throw new Error(err.message);
      }

      const sortedCompetitors = sortCompetitors({
        competitorMetadata,
        competitorOrder: state.competitorOrderConfig,
      });

      commit('setCompetitorMetadata', sortedCompetitors);
      commit('setCompetitorKeyToTypeMap', getters.competitors);
      const selectedCompetitorKeys = !rootState.gridView.selectedCompetitors
        ? []
        : rootState.gridView.selectedCompetitors.map(competitor => competitor.competitorKey);
      const selectedCompetitorsAfterUpdate = isEmpty(selectedCompetitorKeys)
        ? []
        : getters.nonStoreGroupCompetitors.filter(competitor =>
            selectedCompetitorKeys.includes(competitor.competitorKey)
          );
      const defaultCompetitorsDisplayCount =
        rootState.clientConfig.toggleLogic.defaultCompetitorsDisplayCount;
      const newSelectedCompetitors =
        selectedCompetitorsAfterUpdate.length === defaultCompetitorsDisplayCount
          ? selectedCompetitorsAfterUpdate
          : getters.nonStoreGroupCompetitors.slice(0, defaultCompetitorsDisplayCount);
      dispatch('gridView/setSelectedCompetitors', newSelectedCompetitors, { root: true });
      commit('setLoading', false);
    },

    async createCompetitorMetadata({ dispatch, rootState }, competitorMetadata) {
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err, response] = await to(
        axios.post(`/api/competitor-metadata/workpackage/${workpackageId}`, competitorMetadata)
      );
      if (err) throw new Error(err.message);
      dispatch('fetchCompetitorMetadata');
      return response.data;
    },

    async updateCompetitorMetadata({ commit, dispatch }, { updates = {} } = {}) {
      commit('setLoading', true);
      const [err] = await to(axios.patch('/api/competitor-metadata', updates));
      if (err) throw new Error(err.message);
      dispatch('fetchCompetitorMetadata');
    },

    async editCompetitor(
      { commit, dispatch, rootState },
      { competitorKey, competitorDisplayDescription } = {}
    ) {
      commit('setLoading', true);
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err] = await to(
        axios.patch(
          `/api/competitor-metadata/workpackage/${workpackageId}/competitor/${competitorKey}`,
          { competitorDisplayDescription }
        )
      );
      if (err) throw new Error(err.message);
      dispatch('fetchCompetitorMetadata');
    },

    async deleteCompetitor({ commit, dispatch, rootState }, { competitorKey }) {
      commit('setLoading', true);
      const workpackageId = rootState.workpackages.selectedWorkpackage._id;
      const [err] = await to(
        axios.delete(
          `/api/competitor-metadata/workpackage/${workpackageId}/competitor/${competitorKey}`
        )
      );
      if (err) throw new Error(err.message);
      dispatch('fetchCompetitorMetadata');
    },

    resetState({ commit }) {
      commit('resetState');
    },
  },
};

export default store;
